/**
 * File: src/api/guest-evidence/index.js
 * Layer: API Client
 * Description: Wraps authenticated guest-evidence API calls used by the guest
 * portal.
 */
import { GUEST_EVIDENCE } from 'constants/api';
import rawAxios from 'axios';
import axios from 'utils/axios';

/**
 * Fetch the authenticated guest evidence request and linked assessment data.
 * @returns {Promise<Object>}
 */
export const getMyGuestEvidence = async () => {
    try {
        const result = await axios.get(GUEST_EVIDENCE.v1.me);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to load guest evidence');
    }
};

/**
 * Save personal and supervision details for the authenticated guest invitation.
 * @param {Object} data - Guest form values and isDraft flag.
 * @returns {Promise<Object>} Updated guest request.
 */
export const updateGuestInformation = async (data) => {
    try {
        const result = await axios.patch(GUEST_EVIDENCE.v1.information, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Unable to save guest information.');
    }
};

/**
 * Fetch guest request records for the selected candidate assessment.
 * @param {string} assessmentCode - Candidate assessment code
 * @param {Object} params - Guest request list filters and pagination
 * @returns {Promise<Object>}
 */
export const getCandidateGuestRequests = async (assessmentCode, params = {}) => {
    try {
        const result = await axios.get(`${GUEST_EVIDENCE.v1.candidateBaseUrl}/${encodeURIComponent(assessmentCode)}/requests`, { params });
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to load guest requests');
    }
};

export const getCandidateGuestOptions = async (assessmentCode, params = {}) => {
    try {
        const result = await axios.get(`${GUEST_EVIDENCE.v1.candidateBaseUrl}/${encodeURIComponent(assessmentCode)}/guest-options`, {
            params
        });
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to load guest options');
    }
};

export const getAssessorGuestOptions = async (assessmentCode, params = {}) => {
    try {
        const result = await axios.get(`${GUEST_EVIDENCE.v1.assessorBaseUrl}/${encodeURIComponent(assessmentCode)}/guest-options`, {
            params
        });
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to load assessor guest options');
    }
};

export const removeCandidateGuestRequest = async (assessmentCode, guestRequestId) => {
    try {
        const result = await axios.delete(
            `${GUEST_EVIDENCE.v1.candidateBaseUrl}/${encodeURIComponent(assessmentCode)}/requests/${encodeURIComponent(guestRequestId)}`
        );
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to remove guest request');
    }
};

/**
 * Create or extend a guest invitation for the selected outcome.
 * @param {Object} data - Guest invitation payload
 * @returns {Promise<Object>}
 */
export const inviteGuest = async (data) => {
    try {
        const result = await axios.post(GUEST_EVIDENCE.v1.invite, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to invite guest');
    }
};

/**
 * Decline guest evidence request.
 * @param {Object} data - Guest invitation payload
 * @returns {Promise<Object>}
 */
export const declineGuestEvidenceRequest = async (data) => {
    try {
        const result = await axios.post(GUEST_EVIDENCE.v1.decline, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to decline request');
    }
};

export const submitGuestEvidenceRequest = async () => {
    try {
        const result = await axios.post(GUEST_EVIDENCE.v1.submit);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to submit guest evidence');
    }
};

export const getGuestEvidenceLibrary = async () => {
    try {
        const result = await axios.get(GUEST_EVIDENCE.v1.evidenceLibrary);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to get guest evidence library');
    }
};

export const createGuestEvidenceUploadUrl = async (data) => {
    try {
        const result = await axios.post(GUEST_EVIDENCE.v1.evidenceUploadUrl, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to create guest evidence upload URL');
    }
};

export const uploadGuestEvidenceToS3 = async ({ uploadUrl, file, headers = {}, onUploadProgress }) => {
    await rawAxios.put(uploadUrl, file, {
        headers,
        withCredentials: false,
        onUploadProgress
    });
};

export const completeGuestEvidenceUpload = async (evidenceId) => {
    try {
        const result = await axios.post(`${GUEST_EVIDENCE.v1.evidenceBaseUrl}/${encodeURIComponent(evidenceId)}/complete`);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to complete guest evidence upload');
    }
};

export const updateGuestEvidenceMetadata = async (evidenceId, data) => {
    try {
        const result = await axios.patch(`${GUEST_EVIDENCE.v1.evidenceBaseUrl}/${encodeURIComponent(evidenceId)}/metadata`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to update guest evidence metadata');
    }
};

export const linkGuestEvidence = async (data) => {
    try {
        const result = await axios.post(GUEST_EVIDENCE.v1.evidenceLink, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to link guest evidence');
    }
};

export const getGuestEvidenceAccessUrl = async (evidenceId, mode = 'preview') => {
    try {
        const result = await axios.get(`${GUEST_EVIDENCE.v1.evidenceBaseUrl}/${encodeURIComponent(evidenceId)}/access-url`, {
            params: {
                mode
            }
        });
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to open guest evidence');
    }
};

export const updateGuestEvidence = async (evidenceId, data = {}) => {
    try {
        const result = await axios.patch(`${GUEST_EVIDENCE.v1.evidenceBaseUrl}/${encodeURIComponent(evidenceId)}`, data);
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to update guest evidence');
    }
};

export const deleteGuestEvidence = async (evidenceId, params = {}) => {
    try {
        const result = await axios.delete(`${GUEST_EVIDENCE.v1.evidenceBaseUrl}/${encodeURIComponent(evidenceId)}`, { params });
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to delete guest evidence');
    }
};

/**
 * Get Guest List for Assessment Per Unit Outcome.
 * @param {Object} data - Assessment guest list payload
 * @returns {Promise<Object>}
 */
export const assessmentGuestList = async (assessmentCode, data) => {
    try {
        const result = await axios.get(`${GUEST_EVIDENCE.v1.baseUrl}/${assessmentCode}/guest-list`, {
            params: data
        });
        return result.data;
    } catch (err) {
        throw new Error(err?.message || 'Failed to get assessment guest list');
    }
};
